import type { DrawingActivity } from "./activityTypes";
import type { GuideSessionState } from "./GuideSessionController";

interface ActivityPanelProps {
  activity: DrawingActivity;
  guideState?: GuideSessionState;
  onPrevious: () => void;
  onNext: () => void;
  onRestart: () => void;
  onToggleGuide: () => void;
  onExit: () => void;
}

export function ActivityPanel({
  activity,
  guideState,
  onPrevious,
  onNext,
  onRestart,
  onToggleGuide,
  onExit,
}: ActivityPanelProps) {
  if (activity.type === "challenge") {
    return (
      <aside className="activity-panel activity-panel--challenge" aria-label="Creative challenge">
        <div>
          <p className="activity-panel__eyebrow">Creative Challenge</p>
          <h2>{activity.title}</h2>
          <p className="activity-panel__prompt">{activity.prompt}</p>
          <p>{activity.encouragement}</p>
        </div>
        <button className="button button--secondary" onClick={onExit} type="button">
          Exit Challenge
        </button>
      </aside>
    );
  }

  if (!guideState) return null;
  const currentStep = activity.steps[guideState.step - 1]!;
  const onLastStep = guideState.step === guideState.totalSteps;

  return (
    <aside className="activity-panel" aria-label="Guided drawing activity">
      <div className="activity-panel__copy">
        <p className="activity-panel__eyebrow">Learn to Draw</p>
        <h2>{activity.title}</h2>
        <p className="activity-panel__step" aria-live="polite">
          Step {guideState.step} of {guideState.totalSteps}
        </p>
        <p>{currentStep.instruction}</p>
      </div>
      <div className="activity-panel__controls" aria-label="Guide controls">
        <button
          className="button button--secondary"
          disabled={guideState.step === 1}
          onClick={onPrevious}
          type="button"
        >
          Previous
        </button>
        <button className="button button--primary" onClick={onNext} type="button">
          {onLastStep ? "Finish Activity" : "Next"}
        </button>
        <button
          aria-pressed={guideState.visible}
          className="button button--secondary"
          onClick={onToggleGuide}
          type="button"
        >
          {guideState.visible ? "Hide Guide" : "Show Guide"}
        </button>
        <button className="button button--secondary" onClick={onRestart} type="button">
          Restart Guide
        </button>
        <button className="button button--quiet" onClick={onExit} type="button">
          Exit Activity
        </button>
      </div>
    </aside>
  );
}
